import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiJson, ApiError } from "../../../lib/api";

import { getTenantUuid } from "../../ingress/tenant"; // adjust path if needed

const TENANT_UUID = getTenantUuid();


type FeedbackListRow = {
  id: string;
  created_at: string;
  slug?: string | null;
  app_id?: string | null;
  ingress_request_id?: string | null;
  run_id?: string | null;
  flow_id?: string | null;
  version_id?: string | null;

  rating?: string | number | null;
  tags?: string[] | null;
  severity?: string | null;
  incident_flag?: boolean | null;

  quality_score?: number | null;
  complaint_category?: string | null;
  provider_name?: string | null;
  provider_model?: string | null;
  node_id?: string | null;
  latency_ms?: number | null;

  task_status?: string | null;
  task_priority?: string | null;
};

type FeedbackDetailResponse = {
  event: any;
  request?: any | null;
  output?: any | null;
};

function safeStr(v: any): string {
  if (v == null) return "";
  return typeof v === "string" ? v : String(v);
}

function fmtDt(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

function pretty(v: any): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function tagPill(t: string) {
  return (
    <span
      key={t}
      className="inline-flex items-center px-2 py-0.5 rounded border text-[11px] bg-white text-gray-700 mr-1 mb-1"
      title={t}
    >
      {t}
    </span>
  );
}

function severityBadge(sev?: string | null, incident?: boolean | null) {
  const s = (sev || "").toLowerCase();
  const base = "inline-flex items-center px-2 py-1 rounded border text-xs";
  if (incident) return <span className={`${base} border-red-200 bg-red-50 text-red-800`}>incident</span>;
  if (s === "needs_review") return <span className={`${base} border-amber-200 bg-amber-50 text-amber-800`}>needs_review</span>;
  if (s === "normal") return <span className={`${base} border-gray-200 bg-gray-50 text-gray-700`}>normal</span>;
  return <span className={`${base} border-gray-200 bg-white text-gray-700`}>{s || "—"}</span>;
}

export default function AdminIngressFeedbackPage() {
  // Filters
  const [slug, setSlug] = useState<string>("");
  const [appId, setAppId] = useState<string>("");
  const [severity, setSeverity] = useState<string>("");
  const [rating, setRating] = useState<string>("");
  const [tag, setTag] = useState<string>("");
  const [incidentOnly, setIncidentOnly] = useState<boolean>(false);

  // Paging
  const [limit, setLimit] = useState<number>(100);
  const [offset, setOffset] = useState<number>(0);

  // List state
  const [rows, setRows] = useState<FeedbackListRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [err, setErr] = useState<string | null>(null);

  // Detail state
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FeedbackDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailErr, setDetailErr] = useState<string | null>(null);

  const queryUrl = useMemo(() => {
    const p = new URLSearchParams();
    p.set("tenant_id", TENANT_UUID);
    p.set("limit", String(limit));
    p.set("offset", String(offset));

    if (slug.trim()) p.set("slug", slug.trim());
    if (appId.trim()) p.set("app_id", appId.trim());
    if (severity.trim()) p.set("severity", severity.trim());
    if (rating.trim()) p.set("rating", rating.trim());
    if (tag.trim()) p.set("tag", tag.trim());
    if (incidentOnly) p.set("incident_only", "true");

    return `/v1/admin/feedback/events?${p.toString()}`;
  }, [slug, appId, severity, rating, tag, incidentOnly, limit, offset]);

  async function loadList() {
    setLoading(true);
    setErr(null);
    try {
      const data = await apiJson<any>(queryUrl);
      // backend returns array
      setRows(Array.isArray(data) ? data : []);
    } catch (e: any) {
      setErr(e instanceof ApiError ? e.message : String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id: string) {
    setDetailLoading(true);
    setDetailErr(null);
    try {
      const d = await apiJson<FeedbackDetailResponse>(`/v1/admin/feedback/events/${encodeURIComponent(id)}?tenant_id=${TENANT_UUID}`);
      setDetail(d);
    } catch (e: any) {
      setDetailErr(e instanceof ApiError ? e.message : String(e));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryUrl]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      setDetailErr(null);
      return;
    }
    loadDetail(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const selectedEvent = detail?.event || null;
  const selectedRequest = detail?.request || null;
  const selectedOutput = detail?.output || null;

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Feedback Triage</h2>
          <div className="text-sm text-gray-600 mt-1">
            Admin inbox over <code>feedback_events</code> (tenant: <code>{TENANT_UUID}</code>)
          </div>
        </div>

        <div className="flex gap-2">
          <button className="px-3 py-2 rounded border text-sm hover:bg-gray-50" onClick={() => loadList()} type="button">
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 border rounded bg-white p-4">
        <div className="text-sm font-medium">Filters</div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="text-xs text-gray-600">Slug</div>
            <input className="mt-1 w-full border rounded p-2 text-sm" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="starter-support-ticket-triage" />
          </div>

          <div>
            <div className="text-xs text-gray-600">App ID</div>
            <input className="mt-1 w-full border rounded p-2 text-sm font-mono" value={appId} onChange={(e) => setAppId(e.target.value)} placeholder="uuid" />
          </div>

          <div>
            <div className="text-xs text-gray-600">Severity</div>
            <select className="mt-1 w-full border rounded p-2 text-sm" value={severity} onChange={(e) => setSeverity(e.target.value)}>
              <option value="">(any)</option>
              <option value="normal">normal</option>
              <option value="needs_review">needs_review</option>
              <option value="incident">incident</option>
            </select>
          </div>

          <div>
            <div className="text-xs text-gray-600">Rating</div>
            <select className="mt-1 w-full border rounded p-2 text-sm" value={rating} onChange={(e) => setRating(e.target.value)}>
              <option value="">(any)</option>
              <option value="up">up</option>
              <option value="down">down</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>

          <div>
            <div className="text-xs text-gray-600">Tag</div>
            <input className="mt-1 w-full border rounded p-2 text-sm" value={tag} onChange={(e) => setTag(e.target.value)} placeholder="incorrect / unsafe / formatting" />
          </div>

          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={incidentOnly} onChange={(e) => setIncidentOnly(e.target.checked)} />
              Incident only
            </label>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <div className="text-xs text-gray-600">Limit</div>
          <select className="border rounded p-2 text-sm" value={String(limit)} onChange={(e) => setLimit(Number(e.target.value))}>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
            <option value="500">500</option>
          </select>

          <div className="ml-auto flex gap-2">
            <button
              className="px-3 py-2 rounded border text-sm hover:bg-gray-50 disabled:opacity-60"
              type="button"
              onClick={() => setOffset((o) => Math.max(0, o - limit))}
              disabled={offset === 0}
            >
              Prev
            </button>
            <button className="px-3 py-2 rounded border text-sm hover:bg-gray-50" type="button" onClick={() => setOffset((o) => o + limit)}>
              Next
            </button>
          </div>
        </div>
      </div>

      {err && <div className="mt-4 text-sm text-red-600 whitespace-pre-wrap">{err}</div>}

      {/* Two-column layout: inbox + detail */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Inbox */}
        <div className="lg:col-span-5 border rounded bg-white overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="text-sm font-medium">Inbox</div>
            <div className="text-xs text-gray-500">
              showing {rows.length} item{rows.length === 1 ? "" : "s"} (offset {offset})
            </div>
          </div>

          {loading ? (
            <div className="p-4 text-sm text-gray-600">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-4 text-sm text-gray-600">No feedback found.</div>
          ) : (
            <div className="max-h-[740px] overflow-auto">
              {rows.map((r) => {
                const active = r.id === selectedId;
                const rtags = Array.isArray(r.tags) ? r.tags : [];
                const title = r.slug || "(no slug)";
                return (
                  <button
                    key={r.id}
                    className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 ${active ? "bg-gray-50" : ""}`}
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-medium truncate">{title}</div>
                      {severityBadge(r.severity, r.incident_flag)}
                    </div>

                    <div className="mt-1 text-[11px] text-gray-500">
                      {fmtDt(r.created_at)} · rating: <span className="font-mono">{safeStr(r.rating || "—")}</span>{" "}
                      {r.ingress_request_id ? (
                        <>
                          · req <span className="font-mono">{safeStr(r.ingress_request_id).slice(0, 8)}…</span>
                        </>
                      ) : null}
                    </div>

                    {rtags.length > 0 ? <div className="mt-2">{rtags.slice(0, 6).map(tagPill)}</div> : null}

                    <div className="mt-2 text-[11px] text-gray-500">
                      derived:{" "}
                      <span className="font-mono">
                        q={r.quality_score == null ? "—" : String(r.quality_score)} · cat={r.complaint_category || "—"} · provider=
                        {r.provider_name || "—"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-7 border rounded bg-white overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="text-sm font-medium">Detail</div>
            {selectedId ? <div className="text-xs text-gray-500 font-mono">{selectedId}</div> : <div className="text-xs text-gray-500">Select an item</div>}
          </div>

          {!selectedId ? (
            <div className="p-4 text-sm text-gray-600">Select a feedback item from the inbox.</div>
          ) : detailLoading ? (
            <div className="p-4 text-sm text-gray-600">Loading detail…</div>
          ) : detailErr ? (
            <div className="p-4 text-sm text-red-600 whitespace-pre-wrap">{detailErr}</div>
          ) : !detail ? (
            <div className="p-4 text-sm text-gray-600">No detail loaded.</div>
          ) : (
            <div className="p-4 space-y-5">
              {/* Event summary */}
              <div>
                <div className="text-sm font-medium">Feedback</div>
                <div className="mt-2 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    {severityBadge(selectedEvent?.severity, selectedEvent?.incident_flag)}
                    <span className="text-xs text-gray-500">
                      rating: <code>{safeStr(selectedEvent?.rating)}</code>
                    </span>
                    {selectedEvent?.slug ? (
                      <span className="text-xs text-gray-500">
                        slug: <code>{safeStr(selectedEvent.slug)}</code>
                      </span>
                    ) : null}
                  </div>

                  {Array.isArray(selectedEvent?.tags) && selectedEvent.tags.length > 0 ? (
                    <div className="mt-2">{selectedEvent.tags.map(tagPill)}</div>
                  ) : null}

                  {selectedEvent?.comment ? (
                    <div className="mt-3">
                      <div className="text-xs text-gray-600">Comment</div>
                      <div className="mt-1 border rounded bg-gray-50 p-3 text-sm whitespace-pre-wrap">{safeStr(selectedEvent.comment)}</div>
                    </div>
                  ) : null}

                  {selectedEvent?.corrected_output ? (
                    <div className="mt-3">
                      <div className="text-xs text-gray-600">Corrected output</div>
                      <pre className="mt-1 border rounded bg-gray-50 p-3 text-xs overflow-auto max-h-[180px]">
                        {safeStr(selectedEvent.corrected_output)}
                      </pre>
                    </div>
                  ) : null}

                  {selectedEvent?.expected_output ? (
                    <div className="mt-3">
                      <div className="text-xs text-gray-600">Expected / ground truth</div>
                      <pre className="mt-1 border rounded bg-gray-50 p-3 text-xs overflow-auto max-h-[180px]">
                        {safeStr(selectedEvent.expected_output)}
                      </pre>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Links */}
              <div className="border rounded p-3 bg-white">
                <div className="text-sm font-medium">Links</div>
                <div className="mt-2 text-xs text-gray-700 space-y-1">
                  <div>
                    app_id: <code>{safeStr(selectedEvent?.app_id_text || selectedEvent?.app_id)}</code>
                  </div>
                  <div>
                    request_id: <code>{safeStr(selectedEvent?.ingress_request_id_text || selectedEvent?.ingress_request_id)}</code>
                  </div>
                  {selectedEvent?.run_id_text || selectedEvent?.run_id ? (
                    <div>
                      run_id: <code>{safeStr(selectedEvent?.run_id_text || selectedEvent?.run_id)}</code>
                    </div>
                  ) : null}
                  {selectedEvent?.flow_id_text || selectedEvent?.flow_id ? (
                    <div>
                      flow_id: <code>{safeStr(selectedEvent?.flow_id_text || selectedEvent?.flow_id)}</code>
                    </div>
                  ) : null}
                  {selectedEvent?.version_id_text || selectedEvent?.version_id ? (
                    <div>
                      version_id: <code>{safeStr(selectedEvent?.version_id_text || selectedEvent?.version_id)}</code>
                    </div>
                  ) : null}

                  {/* Deep link to ingress request admin page if you have it */}
                  {selectedEvent?.ingress_request_id_text || selectedEvent?.ingress_request_id ? (
                    <div className="pt-2">
                      <Link
                        className="text-blue-700 hover:underline"
                        to={`/app/admin/ingress/requests/${encodeURIComponent(
                          safeStr(selectedEvent?.ingress_request_id_text || selectedEvent?.ingress_request_id)
                        )}`}
                        target="_blank"
                      >
                        Open request in Admin →
                      </Link>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Request snapshot */}
              <div>
                <div className="text-sm font-medium">Request snapshot</div>
                {!selectedRequest ? (
                  <div className="mt-2 text-sm text-gray-600">No request snapshot returned.</div>
                ) : (
                  <details className="mt-2" open>
                    <summary className="cursor-pointer text-xs text-gray-600">Inputs / Normalized Inputs</summary>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs text-gray-600">inputs</div>
                        <pre className="mt-1 border rounded bg-gray-50 p-3 text-xs overflow-auto max-h-[240px]">{pretty(selectedRequest.inputs)}</pre>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">normalized_inputs</div>
                        <pre className="mt-1 border rounded bg-gray-50 p-3 text-xs overflow-auto max-h-[240px]">{pretty(selectedRequest.normalized_inputs)}</pre>
                      </div>
                    </div>
                  </details>
                )}
              </div>

              {/* Output snapshot */}
              <div>
                <div className="text-sm font-medium">Output snapshot</div>
                {!selectedOutput ? (
                  <div className="mt-2 text-sm text-gray-600">No output snapshot returned.</div>
                ) : (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-gray-600">output_json / policy_summary</summary>
                    <pre className="mt-2 border rounded bg-gray-50 p-3 text-xs overflow-auto max-h-[420px]">{pretty(selectedOutput)}</pre>
                  </details>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}